import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  listTests,
  deleteTest,
  createTest,
  TutorTestSummary,
  CreateTestRequest,
} from '../services/tutorScheduledTestService';
import CreateTestForm from '../components/tutor/test/CreateTestForm';

export default function TutorScheduledTestsPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const cId = Number(courseId);

  const [tests, setTests] = useState<TutorTestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadTests = useCallback(() => {
    setLoading(true);
    setError(null);
    listTests(cId)
      .then(setTests)
      .catch(() => setError('Failed to load tests'))
      .finally(() => setLoading(false));
  }, [cId]);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  const handleCreate = async (req: CreateTestRequest) => {
    await createTest(cId, req);
    setShowCreateForm(false);
    loadTests();
  };

  const handleDelete = async (testId: number, attemptCount: number) => {
    if (attemptCount > 0) {
      alert('Cannot delete a test that has student attempts.');
      return;
    }
    if (!window.confirm('Delete this test? This cannot be undone.')) return;
    setDeletingId(testId);
    try {
      await deleteTest(cId, testId);
      loadTests();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to delete test';
      setError(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const modeLabel = (mode: string) =>
    mode === 'SHARED' ? 'Shared' : 'Per-Student';

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Scheduled Tests</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm((v) => !v)}
        >
          {showCreateForm ? 'Cancel' : 'New Test'}
        </Button>
      </Box>

      <Collapse in={showCreateForm}>
        <Paper sx={{ mb: 3, p: 1 }}>
          <CreateTestForm
            courseId={cId}
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
          />
        </Paper>
      </Collapse>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : tests.length === 0 ? (
        <Typography color="text.secondary">No scheduled tests yet.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Scheduled Date</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell align="right">Pool Size</TableCell>
                <TableCell align="right">Attempts</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map((test) => (
                <TableRow key={test.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{test.name}</Typography>
                    {test.warnings.length > 0 && (
                      <Typography variant="caption" color="warning.main">
                        {test.warnings[0]}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{test.scheduledDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={modeLabel(test.randomizationMode)}
                      size="small"
                      color={test.randomizationMode === 'SHARED' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{test.effectiveQuestionCount}</TableCell>
                  <TableCell align="right">{test.totalPoolSize}</TableCell>
                  <TableCell align="right">{test.attemptCount}</TableCell>
                  <TableCell align="right">
                    <Tooltip
                      title={
                        test.attemptCount > 0
                          ? 'Cannot delete — students have attempted this test'
                          : 'Delete test'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={test.attemptCount > 0 || deletingId === test.id}
                          onClick={() => handleDelete(test.id, test.attemptCount)}
                        >
                          {deletingId === test.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
